import { Add, Category, ScreenShare } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Product } from "../../data/models/product/ProductModel";
import { useProducts } from "../hooks/useProducts";
import CategoriesList from "./components/CategoriesList";
import CreateCategoryDialog from "./components/CreateCategoryDialog";
import CreateEditProductDialog from "./components/CreateEditProductDialog";
import CreateStationDialog from "./components/CreateStationDialog";
import ProductsTable from "./components/ProductsTable";
import StationsList from "./components/StationsList";
import StatsCards from "./components/StatsCards";

export default function ProductsPage() {
  const {
    categories,
    products,
    stations,
    totalCategories,
    totalProducts,
    createCategory,
    deleteCategory,
    createProduct,
    updateProduct,
    deleteProduct,
    createStation,
    deleteStation,
    loading,
  } = useProducts();

  const [openCategory, setOpenCategory] = useState(false);
  const [openStation, setOpenStation] = useState(false);
  const [openProduct, setOpenProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setOpenProduct(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setOpenProduct(true);
  };

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 4 },
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent={{ xs: "flex-start", md: "space-between" }}
        gap={2}
        mb={3}
      >
        <Typography variant="h5" fontWeight={700}>
          Gestión de productos
        </Typography>

        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<Category />}
            sx={{ borderRadius: 999 }}
            onClick={() => setOpenCategory(true)}
          >
            Nueva categoría
          </Button>
          <Button
            variant="outlined"
            startIcon={<ScreenShare />}
            sx={{ borderRadius: 999 }}
            onClick={() => setOpenStation(true)}
          >
            Nueva pantalla
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ borderRadius: 999 }}
            onClick={handleCreateProduct}
          >
            Nuevo producto
          </Button>
        </Stack>
      </Stack>

      <StatsCards
        totalProducts={totalProducts}
        totalCategories={totalCategories}
      />

      <CategoriesList categories={categories} deleteCategory={deleteCategory} />

      <StationsList stations={stations} deleteStation={deleteStation} />

      <ProductsTable
        products={products}
        categories={categories}
        stations={stations}
        deleteProduct={deleteProduct}
        onEdit={handleEditProduct}
      />

      <CreateCategoryDialog
        open={openCategory}
        onClose={() => setOpenCategory(false)}
        onSave={async (name) => {
          await createCategory(name);
          setOpenCategory(false);
        }}
        loading={loading}
      />

      <CreateStationDialog
        open={openStation}
        onClose={() => setOpenStation(false)}
        onSave={async (name) => {
          await createStation(name);
          setOpenStation(false);
        }}
        loading={loading}
      />

      <CreateEditProductDialog
        open={openProduct}
        onClose={() => setOpenProduct(false)}
        categories={categories}
        stations={stations}
        initialValue={editingProduct || undefined}
        onSave={async (data) => {
          if (editingProduct) {
            await updateProduct(editingProduct.id!, data);
          } else {
            await createProduct(data as any);
          }
          setOpenProduct(false);
        }}
        loading={loading}
      />
    </Box>
  );
}
